import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerAccessSummary } from '@accestra/types';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import {
  copyTextToClipboard,
  partnerRoleLabel,
  partnerStatusBadgeVariant,
  partnerStatusLabel,
} from '../../lib/partner-portal';

export const portalTableHeaderStyle: CSSProperties = {
  padding: '10px 14px',
  fontWeight: 700,
  fontSize: 11,
  color: 'var(--acc-color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid var(--acc-color-border)',
  background: 'var(--surface-secondary)',
  textAlign: 'left',
};

export const portalTableCellStyle: CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid var(--acc-color-border)',
  verticalAlign: 'top',
  fontSize: 13,
};

export function PartnerHeaderMeta({ access }: { access: PartnerAccessSummary }) {
  const { t } = useTranslation('partnerPortal');

  return (
    <>
      <Badge variant={partnerStatusBadgeVariant(access.partnerStatus)}>
        {partnerStatusLabel(access.partnerStatus, t)}
      </Badge>
      <Badge variant="info">
        {partnerRoleLabel(access.role, t)}
      </Badge>
    </>
  );
}

export function PartnerReferralLinkCard({
  label,
  title,
  description,
  note,
  referralLink,
  copyLabel,
  tone = 'default',
  onCopied,
  onCopyFailed,
}: {
  label: string;
  title?: string;
  description?: string;
  note?: string;
  referralLink: string;
  copyLabel: string;
  tone?: 'default' | 'hero';
  onCopied?: () => void;
  onCopyFailed?: () => void;
}) {
  const isHero = tone === 'hero';

  const handleCopy = async () => {
    const copied = await copyTextToClipboard(referralLink);
    if (copied) {
      onCopied?.();
    } else {
      onCopyFailed?.();
    }
  };

  return (
    <Card
      style={isHero ? {
        border: '1px solid rgba(20, 200, 198, 0.2)',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(16, 43, 67, 0.96) 55%, rgba(20, 200, 198, 0.2) 100%)',
        boxShadow: '0 28px 48px rgba(7, 18, 32, 0.16)',
      } : undefined}
    >
      <div style={{ display: 'grid', gap: isHero ? 16 : 12 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: isHero ? 'rgba(183, 244, 241, 0.88)' : 'var(--acc-color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            {label}
          </div>
          {title ? (
            <div style={{
              fontSize: isHero ? 24 : 16,
              fontWeight: 800,
              color: isHero ? '#f8fdff' : 'var(--acc-color-text)',
              letterSpacing: isHero ? '-0.03em' : '-0.01em',
            }}>
              {title}
            </div>
          ) : null}
          {description ? (
            <div style={{
              maxWidth: 720,
              fontSize: isHero ? 14 : 13,
              lineHeight: 1.6,
              color: isHero ? 'rgba(224, 244, 255, 0.84)' : 'var(--acc-color-text-muted)',
            }}>
              {description}
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div
            style={{
              flex: '1 1 420px',
              minWidth: 0,
              padding: isHero ? '16px 18px' : '12px 14px',
              borderRadius: 16,
              border: isHero ? '1px solid rgba(183, 244, 241, 0.16)' : '1px solid var(--acc-color-border)',
              background: isHero ? 'rgba(255, 255, 255, 0.08)' : 'var(--surface-secondary)',
              fontSize: isHero ? 14 : 13,
              fontWeight: isHero ? 700 : 500,
              color: isHero ? '#f8fdff' : 'var(--acc-color-text)',
              wordBreak: 'break-all',
              boxShadow: isHero ? 'inset 0 1px 0 rgba(255, 255, 255, 0.06)' : 'none',
            }}
          >
            {referralLink}
          </div>
          <Button variant={isHero ? 'primary' : 'secondary'} onClick={() => void handleCopy()}>
            {copyLabel}
          </Button>
        </div>
        {note ? (
          <div style={{ fontSize: 12, color: isHero ? 'rgba(224, 244, 255, 0.78)' : 'var(--acc-color-text-muted)' }}>
            {note}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function PartnerPagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
        Page {page} of {totalPages}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export function PortalSectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--acc-color-text)' }}>
            {title}
          </div>
          {action}
        </div>
        {children}
      </div>
    </Card>
  );
}
